import { alpha, Chip, useTheme } from '@mui/material';
import {
  CircleCheck,
  CircleX,
  LoaderCircle,
  type LucideIcon,
} from 'lucide-react';

import type { WorkflowRunResult } from '$api/WorkflowRunsAPI';

type Tone = 'error' | 'success' | 'warning';

type ResultMeta = {
  label: string;
  tone: Tone;
  icon: LucideIcon;
};

export const WORKFLOW_RUN_RESULT_META: Record<WorkflowRunResult, ResultMeta> = {
  failed: { label: 'Failed', tone: 'error', icon: CircleX },
  inProcess: {
    label: 'In progress',
    tone: 'warning',
    icon: LoaderCircle,
  },
  success: { label: 'Success', tone: 'success', icon: CircleCheck },
};

export function WorkflowRunStatusBadge({
  result,
  size = 'small',
}: {
  result: WorkflowRunResult;
  size?: 'small' | 'medium';
}) {
  const theme = useTheme();
  const meta = WORKFLOW_RUN_RESULT_META[result];
  const palette = theme.palette[meta.tone];
  const Icon = meta.icon;
  const isInProgress = result === 'inProcess';

  return (
    <Chip
      aria-label={`Workflow run status: ${meta.label}`}
      icon={
        <Icon
          aria-hidden
          className={
            isInProgress ? 'workflow-run-status-badge__spinner' : undefined
          }
          size={size === 'small' ? 13 : 15}
        />
      }
      label={meta.label}
      size="small"
      sx={{
        height: size === 'small' ? 22 : 26,
        color: 'text.primary',
        bgcolor: alpha(palette.main, 0.12),
        border: `1px solid ${alpha(palette.main, 0.42)}`,
        fontSize: size === 'small' ? 11.5 : 12,
        fontWeight: 650,
        letterSpacing: 0.05,
        '& .MuiChip-icon': {
          ml: size === 'small' ? 0.625 : 0.75,
          color: palette.light,
        },
        '& .MuiChip-label': {
          px: size === 'small' ? 0.75 : 1,
        },
        '& .workflow-run-status-badge__spinner': {
          animation: 'workflow-run-status-spin 1.4s linear infinite',
        },
        '@keyframes workflow-run-status-spin': {
          to: { transform: 'rotate(360deg)' },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '& .workflow-run-status-badge__spinner': {
            animation: 'none',
          },
        },
      }}
    />
  );
}
